// More → Notifications: two questions and six switches (TCSD-0189 ruling 8,
// item #650). Saved into the person's existing settings row. The per-event
// channel matrix is no longer edited from a screen; its columns stay.
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { errorMessage } from '../../shared/errors';
import { NEWS_KINDS, useNewsPrefs, type NewsCadence } from './news';

type Loud = 'app' | 'chat' | 'email';

export default function NotificationSettings() {
  const { matrix, cadence, off, save, loaded, error } = useNewsPrefs();
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const m = matrix ?? {};
  const loud: Loud = m.email?.enabled === true ? 'email' : m.chat_dm?.enabled === false ? 'app' : 'chat';

  const write = async (next: typeof m, what: string) => {
    setSaveError(null);
    try {
      await save(next);
      setSaved(what);
      window.setTimeout(() => setSaved(null), 1800);
    } catch (e) {
      setSaveError(errorMessage(e, 'That did not save.'));
    }
  };

  const setLoud = (v: Loud) =>
    write(
      {
        ...m,
        chat_dm: { ...(m.chat_dm ?? {}), enabled: v !== 'app' },
        email: { ...(m.email ?? {}), enabled: v === 'email' },
      },
      'Saved',
    );
  const setCadence = (v: NewsCadence) => write({ ...m, news: { ...(m.news ?? {}), cadence: v } }, 'Saved');
  const flip = (kind: string) => {
    const next = new Set(off);
    if (next.has(kind)) next.delete(kind);
    else next.add(kind);
    return write({ ...m, news: { ...(m.news ?? {}), off: Array.from(next) } }, 'Saved');
  };

  const seg = (on: boolean) =>
    `min-h-[40px] flex-1 rounded-lg border text-[13px] ${on ? 'border-tcs-inverse bg-tcs-inverse font-semibold text-tcs-inverse-ink' : 'border-tcs-border bg-tcs-bg text-tcs-text-secondary'}`;

  return (
    <div className="min-h-full bg-tcs-bg-muted">
      <div className="mx-auto max-w-xl px-4 pb-16 pt-3">
        <h1 className="text-[17px] font-semibold text-tcs-text">Notifications</h1>
        {(error || saveError) && <p className="mt-2 rounded-md bg-st-danger-bg px-3 py-2 text-[13px] text-st-danger-fg">{saveError ?? error}</p>}
        {saved && <p role="status" className="mt-2 text-[12.5px] text-st-ok-fg">{saved}</p>}
        {!loaded ? (
          <p className="mt-4 text-[13px] text-tcs-text-muted">Loading…</p>
        ) : (
          <>
            <div className="mt-3 rounded-xl border border-tcs-border bg-tcs-bg p-3">
              <div className="text-[13.5px] font-semibold text-tcs-text">Things to do</div>
              <div className="text-[12px] text-tcs-text-secondary">Always in My Work. This only sets how loudly.</div>
              <div className="mt-2 flex gap-1.5" role="group" aria-label="Things to do">
                {(
                  [
                    ['app', 'In the app'],
                    ['chat', '+ Chat'],
                    ['email', '+ email'],
                  ] as const
                ).map(([k, l]) => (
                  <button key={k} type="button" aria-pressed={loud === k} onClick={() => void setLoud(k)} className={seg(loud === k)}>
                    {l}
                  </button>
                ))}
              </div>
              <div className="mt-4 text-[13.5px] font-semibold text-tcs-text">News</div>
              <div className="text-[12px] text-tcs-text-secondary">Things that happened on what you follow.</div>
              <div className="mt-2 flex gap-1.5" role="group" aria-label="News">
                {(
                  [
                    ['off', 'Off'],
                    ['daily', 'Once a day'],
                    ['weekly', 'Weekly'],
                  ] as const
                ).map(([k, l]) => (
                  <button key={k} type="button" aria-pressed={cadence === k} onClick={() => void setCadence(k)} className={seg(cadence === k)}>
                    {l}
                  </button>
                ))}
              </div>
            </div>

            <div className="mb-1.5 mt-4 px-0.5 text-[11px] font-bold uppercase tracking-[0.07em] text-tcs-text-muted">What counts as news for me</div>
            <div className="overflow-hidden rounded-xl border border-tcs-border bg-tcs-bg">
              {NEWS_KINDS.map(([k, l]) => {
                const on = !off.has(k);
                return (
                  <button
                    key={k}
                    type="button"
                    role="switch"
                    aria-checked={on}
                    onClick={() => void flip(k)}
                    className="flex min-h-[48px] w-full items-center justify-between gap-3 border-b border-tcs-border px-3 text-left text-[13.5px] text-tcs-text last:border-b-0"
                  >
                    <span>{l}</span>
                    <span className={`relative h-5 w-9 shrink-0 rounded-full ${on ? 'bg-tcs-accent-ink' : 'bg-tcs-border-strong'}`}>
                      <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-tcs-bg transition-all ${on ? 'right-0.5' : 'left-0.5'}`} />
                    </span>
                  </button>
                );
              })}
            </div>
            <p className="mt-3 text-center text-[12px] text-tcs-text-muted">
              Two questions, six switches. <Link className="underline" to="/my-work/news">See what counts as news</Link>
            </p>
          </>
        )}
      </div>
    </div>
  );
}
